import "@redotech/money/currencies"; // Necessary to fix "inferred type not portable" type errors
import { Currency } from "@redotech/money/currencies";

import {
  AddressSchema,
  CompensationMethodType,
  DraftReturnSchema,
  DraftReturnShipmentSchema,
  InteractiveMapLocationSchema,
  ReturnTotalCalculationsOutputSchema,
  ShipmentMethodSchema,
} from "@redotech/redo-model/draft-return/draft-return";
import {
  NewExchangeItemSchema,
  ReturnableItemSchema,
} from "@redotech/redo-model/draft-return/draft-return-items";
import {
  faqArticleBodySchema,
  faqCollectionBodySchema,
  faqThemeBodySchema,
} from "@redotech/redo-model/faq/faq-body-schema";
import { ShippingRateTableSchema } from "@redotech/redo-model/integration/redo-in-shipping/shipping-rate-table";
import { Provider } from "@redotech/redo-model/order";
import { redoRecommendedProductSchema } from "@redotech/redo-model/redo-recommended-product-schema";
import { ChannelZod, ProvisionType } from "@redotech/redo-model/return";
import {
  ProductTotalsSchema,
  ReturnTotalsSchema,
} from "@redotech/redo-model/return-totals-calculator";
import { ItemSchema } from "./common/item-schema";

import { WarrantyRegistrationSchema } from "@redotech/redo-model/warranty-registration";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export const changeAddress = {
  name: "changeAddress" as const,
  input: z.object({
    draftReturnId: z.string(),
    newOrderAddress: AddressSchema.nullish(),
    returnAddress: AddressSchema.nullish(),
    shouldUpdateCustomer: z.boolean().nullish(),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const editReturnableItem = {
  name: "editReturnableItem" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    item: ReturnableItemSchema,
  }),
  output: z.object({ draftReturn: DraftReturnSchema, newItemId: z.string() }),
};

export const editPendingItemQuantity = {
  name: "editPendingItemQuantity" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    pendingItemId: z.string(),
    updatedQuantity: z.number(),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const getDraftReturn = {
  name: "getDraftReturn" as const,
  input: z.object({ draftReturnId: zExt.objectId() }),
  output: z.object({ draftReturn: DraftReturnSchema.nullable() }),
};

export const getShippingEstimate = {
  name: "getShippingEstimate" as const,
  input: z.object({ draftReturnId: zExt.objectId() }),
  output: z.array(DraftReturnShipmentSchema),
};

export const getRecommendedProductsByProvider = {
  name: "getRecommendedProductsByProvider" as const,
  input: z.object({
    provider: createEnum(Provider),
    excludedVariantIds: z.array(z.string()),
    excludedParentProductIds: z.array(z.string()),
    numRecommendations: z.number(),
    customer: z.object({ email: z.string().nullish() }),
    ccSiteId: z.string().optional(),
    recommendedProductFilterId: z.string().optional(),
    /** Product IDs for provider recommendations strategy (e.g., from order line items) */
    productIds: z.array(z.string()).optional(),
  }),
  output: z.object({ products: z.array(redoRecommendedProductSchema) }),
};

export const getStripePublicKey = {
  name: "getStripePublicKey" as const,
  input: z.object({}).nullish(),
  output: z.object({
    publicKey: z.string(),
    connectedAccountId: z.string().nullish(),
  }),
};

export const setShipmentMethodInfo = {
  name: "setShipmentMethodInfo" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    shipmentMethod: ShipmentMethodSchema,
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const setCompensationMethod = {
  name: "setCompensationMethod" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    selectedMethod: createEnum(CompensationMethodType).nullable(),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const removeReturnItem = {
  name: "removeReturnItem" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    pendingItemId: z.string(),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const rejectReturnItems = {
  name: "rejectReturnItems" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    returnableItemIds: z.array(z.string()),
    rejectReason: z.string(),
    rejectMessage: z.string(),
    flowPath: z.array(z.number()).readonly().optional(),
    multipleChoiceAnswers: z
      .array(z.object({ step: z.number(), answer: z.number() }))
      .readonly()
      .optional(),
    channel: ChannelZod.optional(),
  }),
  output: z.object({}),
};

export const addNewItems = {
  name: "addNewItems" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    items: z.array(NewExchangeItemSchema),
    clearExistingItems: z.boolean().optional(),
    cartToken: z.string().nullish(),
    cartCoupons: z.array(z.string()).nullish(),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const removeNewItems = {
  name: "removeNewItems" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    variantIds: z.array(z.string()),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const PhysicalAddressSchema = z.object({
  street1: z.string(),
  street2: z.string().nullish(),
  state: z.string().nullish(),
  city: z.string(),
  country: z.string(),
  zip: z.string(),
});
export interface PhysicalAddress
  extends z.infer<typeof PhysicalAddressSchema> {}

export const verifyAddress = {
  name: "verifyAddress" as const,
  input: z.object({ address: PhysicalAddressSchema }),
  output: z.object({
    originalAddress: PhysicalAddressSchema,
    verifiedAddress: PhysicalAddressSchema,
    verifications: z.object({ delivery: z.object({ success: z.boolean() }) }),
    isInternational: z.boolean(),
    avc: z.string().optional(),
  }),
};

export const calculateReturnTotals = {
  name: "calculateReturnTotals" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    usePresentmentCurrency: z.boolean().optional(),
  }),
  output: z.object({ calculations: ReturnTotalCalculationsOutputSchema }),
};

export const calculateReturnTotalsForReturn = {
  name: "calculateReturnTotalsForReturn" as const,
  input: z.object({
    returnId: zExt.objectId(),
    advancedExchangeItemOverride: z.array(ItemSchema).optional(),
  }),
  output: z.object({
    totals: ReturnTotalsSchema,
    productTotals: ProductTotalsSchema,
  }),
};

export const getLocations = {
  name: "getLocations" as const,
  input: z.object({
    draftReturnId: zExt.objectId().nullish(),
    returnId: zExt.objectId().nullish(),
  }),
  output: z.object({
    locations: z.array(InteractiveMapLocationSchema).optional(),
  }),
};

export const getSatisfactionResponse = {
  name: "getSatisfactionResponse" as const,
  input: z.object({ conversationId: z.string() }),
  output: z.object({
    rating: z.number().nullish(),
    comment: z.string().nullish(),
    createdAt: z.date().nullish(),
  }),
};

export const saveSatisfactionResponse = {
  name: "saveSatisfactionResponse" as const,
  input: z.object({
    channel: z.string(),
    comment: z.string(),
    conversationId: z.string(),
    rating: z.number(),
  }),
  output: z.object({ success: z.boolean() }),
};

export const getFaq = {
  name: "getFaq" as const,
  input: z.object({}).nullish(),
  output: z.object({
    theme: faqThemeBodySchema,
    collections: z.array(faqCollectionBodySchema),
    articles: z.map(z.string(), faqArticleBodySchema),
  }),
};

export const getFaqPage = {
  name: "getFaqPage" as const,
  input: z.object({ pageId: z.string() }),
  output: z.object({
    theme: faqThemeBodySchema,
    collections: z.array(faqCollectionBodySchema),
    articles: z.map(z.string(), faqArticleBodySchema),
  }),
};

export const getWarrantyRegistrationFlow = {
  name: "getWarrantyRegistrationFlow" as const,
  input: z.object({}).nullish(),
  output: z.object({ flow: z.any() }),
};

export const getWarrantyRegistration = {
  name: "getWarrantyRegistration" as const,
  input: z.object({ warrantyId: z.string() }),
  output: z.object({
    warrantyRegistration: WarrantyRegistrationSchema,
    firstName: z.string().nullish(),
  }),
};

const DiscountValueSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("percentage"), value: z.number() }),
  z.object({
    type: z.literal("amount"),
    value: z.object({ amount: z.number(), currencyCode: z.string() }),
  }),
]);

export type DiscountValue = z.infer<typeof DiscountValueSchema>;

const ProductDiscountSchema = z.object({
  product: z.object({
    id: z.string(),
    handle: z.string(),
    title: z.string(),
    featuredImage: z
      .object({ url: z.string().nullish(), altText: z.string().nullish() })
      .nullish(),
    price: z.number(),
  }),
  discount: z.object({
    linkForProduct: z.string().nullish(),
    code: z.string().nullish(),
    value: DiscountValueSchema,
    discountedPrice: z.number(),
  }),
});

export const getRegistrationDiscounts = {
  name: "getRegistrationDiscounts" as const,
  input: z.object({ warrantyRegistrationId: z.string().optional() }),
  output: z.object({ productDiscounts: z.array(ProductDiscountSchema) }),
};

export const getMarketingTriggered = {
  name: "getMarketingTriggered" as const,
  input: z.object({ warrantyRegistrationId: z.string() }),
  output: z.object({
    marketingTriggered: z.object({
      emailSent: z.boolean(),
      value: DiscountValueSchema.nullish(),
    }),
  }),
};

export type ProductDiscount = z.infer<typeof ProductDiscountSchema>;

export const getOrderId = {
  name: "getOrderId" as const,
  input: z.object({ shopifyId: z.string() }),
  output: z.object({ id: zExt.objectId() }),
};

export const setProvisionType = {
  name: "setProvisionType" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    provisionType: createEnum(ProvisionType).nullish(),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const getExchangeShippingOptions = {
  name: "getExchangeShippingOptions" as const,
  input: z.object({ draftReturnId: zExt.objectId() }),
  output: z.object({
    options: z.array(
      z.object({
        rateTable: z.lazy(() => ShippingRateTableSchema),
        name: z.string(),
        price: z.number(),
        currency: createEnum(Currency),
        description: z.string().optional(),
      }),
    ),
  }),
};

export const setExchangeShippingOption = {
  name: "setExchangeShippingOption" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    selectedExchangeShippingOption: z
      .object({
        rateTable: z.lazy(() => ShippingRateTableSchema),
        name: z.string(),
        price: z.number(),
        currency: createEnum(Currency),
      })
      .nullish(),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

export const getOrders = {
  name: "getOrders" as const,
  input: z.object({ orderIds: z.array(zExt.objectId()) }),
  output: z.object({ orders: z.array(z.any()) }), // If you see this and its past May 23rd 2025, then yell at @Anish and change this back to OrderSchema
};

export const isShopAppDisabled = {
  name: "isShopAppDisabled" as const,
  input: z.object({}).nullish(),
  output: z.object({ isDisabled: z.boolean() }),
};

export const updatePickupVariant = {
  name: "updatePickupVariant" as const,
  input: z.object({
    draftReturnId: zExt.objectId(),
    pickupVariant: z.string(),
  }),
  output: z.object({ draftReturn: DraftReturnSchema }),
};

// ProductData schema for Commerce Cloud upsell recommendations
const variantDataSchema = z.object({
  variantId: z.string(),
  productId: z.string().nullable().optional(),
  productTitle: z.string().nullable().optional(),
  variantTitle: z.string(),
  price: z.object({ amount: z.string(), currency: z.string() }),
  compareAtPrice: z
    .object({ amount: z.string(), currency: z.string() })
    .optional(),
  image: z.object({ url: z.string(), altText: z.string().optional() }),
  options: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
      productAsVariant: z.boolean().nullable().optional(),
    }),
  ),
  onlineInventoryAvailable: z.number(),
  isInStock: z.boolean(),
  backorder: z.string().nullable().optional(),
  sku: z.string().nullish(),
  barcode: z.string().nullish(),
  metafields: z.record(z.string(), z.string().nullish()).optional(),
});

const productDataSchema = z.object({
  productId: z.string(),
  productTitle: z.string(),
  images: z
    .array(z.object({ url: z.string(), alt: z.string().nullish() }))
    .nullish(),
  options: z.array(z.object({ name: z.string(), values: z.array(z.string()) })),
  variants: z.array(variantDataSchema),
  shopOnSite: z
    .object({
      onlineStoreUrl: z.string().optional(),
      handle: z.string().optional(),
    })
    .optional(),
});

export const getCommerceCloudUpsellRecommendedProducts = {
  name: "getCommerceCloudUpsellRecommendedProducts" as const,
  input: z.object({
    productIds: z.array(z.string()),
    numRecommendations: z.number(),
  }),
  output: z.object({ products: z.array(productDataSchema) }),
};

export const getBigCommerceUpsellRecommendedProducts = {
  name: "getBigCommerceUpsellRecommendedProducts" as const,
  input: z.object({
    productIds: z.array(z.string()),
    numRecommendations: z.number(),
  }),
  output: z.object({ products: z.array(productDataSchema) }),
};

const detailedProductInfoSchema = z.object({
  id: z.string(),
  title: z.string(),
  handle: z.string(),
  vendor: z.string(),
  onlineStoreUrl: z.string().nullish(),
  totalInventory: z.number().nullish(),
  description: z.string(),
  productType: z.string(),
  collections: z.object({
    nodes: z.array(z.object({ id: z.string(), title: z.string() })),
  }),
  availableForSale: z.boolean(),
  tags: z.array(z.string()),
  publishedAt: z.string(),
  options: z.array(
    z.object({ id: z.string(), name: z.string(), values: z.array(z.string()) }),
  ),
  priceRange: z.object({
    minVariantPrice: z.object({ amount: z.string(), currencyCode: z.string() }),
    maxVariantPrice: z.object({ amount: z.string(), currencyCode: z.string() }),
  }),
  featuredImage: z
    .object({ altText: z.string().nullish(), url: z.string() })
    .nullish(),
  variants: z.object({
    nodes: z.array(
      z.object({
        id: z.string(),
        requiresShipping: z.boolean(),
        currentlyNotInStock: z.boolean(),
        availableForSale: z.boolean(),
        weight: z.number().nullish(),
        weightUnit: z.string().optional(),
        price: z.object({ amount: z.string(), currencyCode: z.string() }),
        sku: z.string().nullish(),
        barcode: z.string().nullish(),
        title: z.string(),
        selectedOptions: z.array(
          z.object({ name: z.string(), value: z.string() }),
        ),
        quantityAvailable: z.number().nullish(),
        image: z.object({ url: z.string() }).nullish(),
        backorderDate: z.object({ value: z.string() }).nullish(),
      }),
    ),
  }),
  images: z.object({
    nodes: z.array(
      z.object({
        altText: z.string().nullish(),
        id: z.string().nullish(),
        url: z.string(),
      }),
    ),
  }),
});

export type DetailedProductInfo = z.infer<typeof detailedProductInfoSchema>;

export const searchExchangeProducts = {
  name: "searchExchangeProducts" as const,
  input: z.object({
    query: z.string().optional(),
    limit: z.number().optional(),
    productIds: z.array(z.string()).optional(),
    returnVariants: z.boolean().optional(),
    provider: createEnum(Provider),
    presentmentCurrency: createEnum(Currency).optional(),
  }),
  output: z.object({
    products: z.array(detailedProductInfoSchema),
    variants: z.array(variantDataSchema).optional(),
  }),
};

export const getRegistrations = {
  name: "getRegistrations" as const,
  input: z.object({ orderId: zExt.objectId().nullish() }),
  output: z.object({ registrations: z.array(WarrantyRegistrationSchema) }),
};

export { getWarrantyEligibleProductsSchema as getWarrantyEligibleProducts } from "@redotech/redo-model/warranties/get-warranty-eligible-products-schema";
export { submitWarrantyRegistrationSchema as submitWarrantyRegistration } from "@redotech/redo-model/warranties/submit-warranty-registration-schema";
export { completeExchangeConversion } from "./complete-exchange-conversion";
export { cancelPickup } from "./pickup/cancel-pickup";
export { reschedulePickup } from "./pickup/reschedule-pickup";
